<script lang="ts">
  import { mdiCalendarBlankOutline, mdiCalendarMultiple } from "@mdi/js"
  import { untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import CreateWorkflowStepFrame from "../../../../../../../../_shared/_modules/create-workflow-step-frame"
  import StepActionCTA from "../../../../../../../../../../_shared/_modules/step-action-cta"
  import BlockerTimingFields from "./_modules/blocker-timing-fields"
  import {
    buildInitialBlockerTimingState,
    normalizeBlockerCalendarDay,
    normalizeBlockerDuration,
    normalizeBlockerStartTime,
    validateBlockerTiming,
  } from "./_shared/_logic/blocker-timing"
  import type {
    BlockerConfigurationOutput,
    BlockerCreateInit,
    BlockerCreateWorkflowEvent,
    BlockerDurationMode,
    BlockerGeneralOutput,
    BlockerPlanningKind,
    BlockerSeriesPattern,
  } from "../../_shared/_logic/create-blocker-contract"

  type Props = {
    init: BlockerCreateInit
    general: BlockerGeneralOutput
    output?: BlockerConfigurationOutput
    readOnly?: boolean
    onBack: () => void
    onEvent: (event: BlockerCreateWorkflowEvent) => void | Promise<void>
    onError?: (message: string | null) => void
  }

  const {
    init,
    output = undefined,
    readOnly = false,
    onBack,
    onEvent,
    onError = () => {},
  }: Props = $props()

  const initialConfiguration = untrack(() => buildInitialBlockerTimingState({ init, output }))

  let planningKind = $state<BlockerPlanningKind>(initialConfiguration.planningKind)
  let durationMode = $state<BlockerDurationMode>(initialConfiguration.durationMode)
  let singleDate = $state<Date | null>(initialConfiguration.singleDate)
  let startTime = $state(initialConfiguration.startTime)
  let durationMinutes = $state<number | null>(initialConfiguration.durationMinutes)
  let seriesRangeStart = $state<Date | null>(initialConfiguration.seriesRangeStart)
  let seriesRangeEnd = $state<Date | null>(initialConfiguration.seriesRangeEnd)
  let seriesPattern = $state<BlockerSeriesPattern>(initialConfiguration.seriesPattern)
  let seriesWeekdays = $state<number[]>(initialConfiguration.seriesWeekdays)
  let seriesIgnoreConflicts = $state(initialConfiguration.seriesIgnoreConflicts)

  const validationMessage = $derived(
    validateBlockerTiming({
      planningKind,
      durationMode,
      singleDate,
      startTime,
      durationMinutes: Number(durationMinutes ?? 0),
      seriesRangeStart,
      seriesRangeEnd,
      seriesPattern,
      seriesWeekdays,
    }),
  )
  const canComplete = $derived(!validationMessage)

  const setPlanningKind = (value: string) => {
    planningKind = value === "single" ? "single" : "series"
  }

  const complete = () => {
    onError(null)

    if (validationMessage) {
      onError(validationMessage)

      return
    }

    const normalizedDuration = normalizeBlockerDuration(durationMinutes)

    if (durationMode === "individual" && !normalizedDuration) {
      onError("Bitte eine gültige Dauer wählen.")

      return
    }

    const normalizedStartTime = normalizeBlockerStartTime(startTime)
    void onEvent({
      type: "configuration.completed",
      output: {
        planningKind,
        durationMode,
        singleDate: normalizeBlockerCalendarDay(singleDate),
        startTime: normalizedStartTime ?? startTime,
        durationMinutes: normalizedDuration,
        seriesRangeStart: normalizeBlockerCalendarDay(seriesRangeStart),
        seriesRangeEnd: normalizeBlockerCalendarDay(seriesRangeEnd),
        seriesPattern,
        seriesWeekdays: [...seriesWeekdays],
        seriesIgnoreConflicts,
      },
    })
  }
</script>

{#snippet planningKindToggle()}
  <ToggleCTA
    darkMode={true}
    value={planningKind}
    allowReselect={false}
    options={[
      { value: "single", label: "Einzel", icon: mdiCalendarBlankOutline },
      { value: "series", label: "Serie", icon: mdiCalendarMultiple },
    ]}
    onChange={setPlanningKind}
    dataTestid="blocker-timing.planning-kind"
  />
{/snippet}

<CreateWorkflowStepFrame
  title="Zeitraum"
  helpModalTextId="blockerCreateTiming"
  buttonSnippet={readOnly ? null : planningKindToggle}
>
  <p class="step-description">
    Legen Sie fest, wann der Kalender blockiert wird. Serien eignen sich für Urlaub oder regelmäßige
    Sperrzeiten.
  </p>
  <BlockerTimingFields
    bind:planningKind
    bind:durationMode
    bind:singleDate
    bind:startTime
    bind:durationMinutes
    bind:seriesRangeStart
    bind:seriesRangeEnd
    bind:seriesPattern
    bind:seriesWeekdays
    {readOnly}
  />
  {#if !readOnly}
    <div class="step-actions">
      <CTA type="secondaryButton" content="Zurück" onClick={onBack} />
      <StepActionCTA
        type="primaryButton"
        content="Weiter zu Details"
        disabled={!canComplete}
        disabledReason={validationMessage}
        onClick={complete}
      />
    </div>
  {/if}
</CreateWorkflowStepFrame>

<style>
  .step-description {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 76%, transparent);
    line-height: 1.45;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }
</style>
